/** Ground zones (ticks, inside-auras, expiry) and telegraph resolution. */
import type { SimContext, UnitId, Zone, ZoneId } from '../contracts';
import { isAirborne } from '../contracts'; // +glide
import { applyAura, removeAura } from './auras';
import { runEffects } from './effects';
import { unitOf } from './util';

function clearInside(ctx: SimContext, z: Zone): void {
  if (z.insideAuraId) for (const uid of z.inside) removeAura(ctx, uid, { auraId: z.insideAuraId, casterId: z.sourceId }, 'expired');
  z.inside = [];
}

/** Occupants sorted by distance to `at` (ties: the given order, which is deterministic). */
function nearestFirst(ctx: SimContext, at: { x: number; y: number }, ids: readonly UnitId[]): UnitId[] {
  const d = (id: UnitId): number => {
    const u = ctx.world.units[id];
    return u ? (u.pos.x - at.x) ** 2 + (u.pos.y - at.y) ** 2 : Infinity;
  };
  return ids.map((id, i) => ({ id, i, d: d(id) })).sort((a, b) => a.d - b.d || a.i - b.i).map((x) => x.id);
}

export function tickZones(ctx: SimContext): void {
  const w = ctx.world;
  const removed = new Set<ZoneId>();
  for (const z of [...w.zones]) {
    if (!unitOf(ctx, z.sourceId)) {
      clearInside(ctx, z);
      removed.add(z.id);
      continue;
    }
    const occupants = ctx.sys.spatial.unitsInShape(w, z.area, { affects: z.affects, relativeTo: z.sourceId });
    if (z.insideAuraId) {
      for (const uid of z.inside) {
        if (!occupants.includes(uid)) removeAura(ctx, uid, { auraId: z.insideAuraId, casterId: z.sourceId }, 'cancelled');
      }
      const inside: string[] = [];
      for (const uid of occupants) {
        if (z.inside.includes(uid) || applyAura(ctx, uid, z.insideAuraId, z.sourceId, z.abilityId, { durationMs: 0 })) inside.push(uid);
      }
      z.inside = inside;
    }
    if (z.tick && z.nextTickAt !== null) {
      while (z.nextTickAt <= ctx.now && z.nextTickAt <= z.expiresAt) {
        // +basekits: a trap springs on the first occupant (nearest first), then ends.
        const hit = z.triggerOnce ? nearestFirst(ctx, z.area.origin, occupants).slice(0, 1) : occupants;
        if (z.triggerOnce && hit.length > 0) z.expiresAt = ctx.now;
        for (const uid of hit) {
          // +jump: a hostile ground zone doesn't tick on a unit in the air.
          const occ = ctx.world.units[uid];
          if (occ && (isAirborne(occ, ctx.now) || occ.perch) && occ.faction !== unitOf(ctx, z.sourceId)?.faction) continue;
          runEffects(ctx, z.tick.effects, { sourceId: z.sourceId, abilityId: z.abilityId, targetId: uid, point: z.area.origin, periodic: true, depth: 1, ...(z.snapshot ? { snapshot: z.snapshot } : {}) });
        }
        z.nextTickAt += Math.max(1, z.tick.intervalMs);
      }
    }
    if (z.expiresAt <= ctx.now) {
      clearInside(ctx, z);
      removed.add(z.id);
      // +basekits: a travelling area's end burst (Wandering Star), where it stopped.
      if (z.onExpire) runEffects(ctx, z.onExpire, { sourceId: z.sourceId, abilityId: z.abilityId, targetId: null, point: { ...z.area.origin }, depth: 1 });
    }
  }
  if (removed.size > 0) w.zones = w.zones.filter((z) => !removed.has(z.id));
}

export function tickTelegraphs(ctx: SimContext): void {
  const w = ctx.world;
  const removed = new Set<string>();
  for (const tg of [...w.telegraphs]) {
    if (!unitOf(ctx, tg.sourceId)) {
      removed.add(tg.id);
      continue;
    }
    const follow = unitOf(ctx, tg.followUnitId);
    if (follow?.alive) {
      tg.area = { ...tg.area, origin: { x: follow.pos.x, y: follow.pos.y }, facing: follow.id === tg.sourceId ? follow.facing : tg.area.facing };
    }
    if (tg.resolvesAt > ctx.now) continue;
    const hitIds = ctx.sys.spatial.unitsInShape(w, tg.area, { affects: tg.affects, relativeTo: tg.sourceId });
    for (const uid of hitIds) {
      runEffects(ctx, tg.effects, { sourceId: tg.sourceId, abilityId: tg.abilityId, targetId: uid, point: tg.area.origin, depth: 1 });
    }
    ctx.emit({ type: 'telegraph_end', telegraphId: tg.id, sourceID: tg.sourceId, abilityGameID: tg.abilityId, hitIds });
    if (tg.repeatsLeft > 0 && tg.repeatIntervalMs > 0) {
      tg.repeatsLeft -= 1;
      tg.resolvesAt += tg.repeatIntervalMs;
    } else {
      removed.add(tg.id);
    }
  }
  if (removed.size > 0) w.telegraphs = w.telegraphs.filter((t) => !removed.has(t.id));
}
